import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { change, Field, reduxForm } from 'redux-form';

import { Typography } from '@material-ui/core';

import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';

import {
  ACTIVITIES_AVAILABLE_PARTICIPANTS,
  ACTIVITY
} from 'graphql/queries/activities';
import {
  CREATE_ACTIVITY_BOOKING,
  UPDATE_ACTIVITY_BOOKING
} from 'graphql/mutations/activities-bookings';

import {
  AppHeader,
  CircularLoader,
  SingleDatePicker,
  FormActionButtons,
  BookInfoList,
  MultiDatePickerCalendar
} from 'components';

import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import {
  getReceivedActivityBookingsDates,
  getReceivedActivityData
} from 'utils/accommodations-and-activities/prepareReceivedActivityData';
import { getParticipants } from 'utils/book-activity/prepareParticipants';
import { getPreparedBookActivityData } from 'utils/book-activity/prepareBookActivityData';
import {
  getAvailableRangeDates,
  getDefaultStartTime,
  getDisabledDates,
  getListOfStartTimeAndDuration,
  getListOfStartTimeAndDurationByCutoffPeriod
} from 'utils/book-activity/availableDates';
import {
  getAvailableParticipantsForStartTime,
  getMaxParticipants
} from 'utils/book-activity/maxParticipants';

import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import validate from './validate';

import useStyles from 'pages/book-activity/styles';

const initialValues = {
  maxParticipants: 0,
  currency: 'NOK',
  participants: [],
  addOns: [],
  accommodations: [],
  pay: {
    'Pay on arrival': true
  },
  scheduleType: null
};

const SCHEDULE_DATE_NO_TIME_NAME = 'scheduleDateForm';
const SCHEDULE_DATE_AND_TIME_NAME = 'scheduleDateTimeForm';
const SCHEDULE_PASS_NAME = 'schedulePassForm';

const BookActivity = ({
  bookActivityForm,
  changeFormFieldValue,
  handleSubmit,
  bookedData,
  isUpdate,
  businessOwner
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const selectedLanguage = i18n.language;
  const ownerId = businessOwner.selectedBusinessOwner.id;

  const activityId = bookActivityForm.values.activityId;

  const initialStartingDate =
    bookedData && bookedData.startingDate ? bookedData.startingDate : moment();

  const [deletedRelatedAccommodations] = React.useState([]);
  const [isLoading, setLoading] = React.useState(null);
  const [activity, setActivity] = React.useState(null);
  const [isUpdatedParticipants, setIsUpdatedParticipants] = React.useState(
    false
  );
  const [
    availableParticipantsData,
    setAvailableParticipantsData
  ] = React.useState(null);

  const [createActivityBooking] = useMutation(CREATE_ACTIVITY_BOOKING);
  const [updateActivityBooking] = useMutation(UPDATE_ACTIVITY_BOOKING);

  const { loading, data } = useQuery(ACTIVITY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: activityId || parseInt(id),
      businessOwnerId: ownerId
    }
  });

  const {
    loading: isAvailableParticipantsLoading,
    data: receivedAvailableParticipantsData,
    refetch: refetchParticipantsData
  } = useQuery(ACTIVITIES_AVAILABLE_PARTICIPANTS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: activityId || parseInt(id),
      businessOwnerId: ownerId,
      startPeriod: moment(initialStartingDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(initialStartingDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (!receivedAvailableParticipantsData) return;

    receivedAvailableParticipantsData.activities.forEach(data => {
      data.availableParticipantsPerDate = data.availableParticipantsPerDate.map(
        participants => ({
          ...participants,
          availableParticipants: 0
        })
      );
    });

    const availableParticipantsData = getReceivedActivityBookingsDates(
      receivedAvailableParticipantsData
    );

    setAvailableParticipantsData(availableParticipantsData);
  }, [receivedAvailableParticipantsData]);

  React.useEffect(() => {
    if (data && data.activities && data.activities.length) {
      const receivedActivity = data.activities[0];
      const activityData = getReceivedActivityData(receivedActivity);

      activityData['pricesType'] = receivedActivity['pricesType'];
      activityData['scheduleType'] = receivedActivity['scheduleType'];

      changeFormFieldValue(`scheduleType`, activityData['scheduleType']);

      if (bookedData) {
        handleListStartTimeAndDurationField(activityData);
        setParticipants(activityData);
      }

      setListOfAvailableCurrencies(receivedActivity);
      setMaxParticipants(activityData['generalForm'].maxParticipants);

      setActivity(activityData);
      setAddOns(activityData);

      if (activityData.scheduleType === SCHEDULE_PASS_NAME) {
        setParticipants(activityData);
      }
    }
  }, [data]);

  React.useEffect(() => {
    const participants = bookActivityForm.values.participants;
    if (participants && participants.length) {
      setParticipants(activity);
    }
  }, [bookActivityForm.values.currency]);

  React.useEffect(() => {
    const multiDates = bookActivityForm.values.multiDates;
    if (multiDates) {
      setParticipants(activity);
    }
  }, [bookActivityForm.values.multiDates]);

  React.useEffect(() => {
    const startingDate = bookActivityForm.values.startingDate;

    if (startingDate) {
      handleListStartTimeAndDurationField(activity);
      setParticipants(activity);
    }
  }, [bookActivityForm.values.startingDate]);

  React.useEffect(() => {
    const startTime = bookActivityForm.values.startTime;

    if (startTime) {
      changeFormFieldValue('maxParticipants', startTime.seatsLeft);
    }
  }, [bookActivityForm.values.startTime]);

  const setListOfAvailableCurrencies = receivedActivity => {
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
      receivedActivity.prices
    );
    const selectedCurrency = bookActivityForm.values.currency;

    if (!listOfAvailableCurrencies.includes(selectedCurrency)) {
      changeFormFieldValue('currency', listOfAvailableCurrencies[0]);
    }
  };

  const handleListStartTimeAndDurationField = activity => {
    if (!activity) return;

    const startingDate = bookActivityForm.values.startingDate;
    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];
    let listOfStartTimeAndDuration = getListOfStartTimeAndDuration(
      availableParticipantsPerDate,
      activity,
      bookActivityForm
    );
    listOfStartTimeAndDuration = getListOfStartTimeAndDurationByCutoffPeriod(
      activity,
      listOfStartTimeAndDuration,
      startingDate
    );
    let selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      bookActivityForm
    );

    if (!selectedStartTime) {
      setMaxParticipants(activity.generalForm.maxParticipants);
      availableParticipantsPerDate.map(item => {
        const formattedStartDate = startingDate
          ? moment(startingDate).format('YYYY-MM-DD')
          : '';
        if (!item.startTime && item.date === formattedStartDate) {
          setMaxParticipants(item.availableParticipants);
        }
      });
    } else {
      setMaxParticipants(selectedStartTime.seatsLeft);

      listOfStartTimeAndDuration = getAvailableParticipantsForStartTime(
        listOfStartTimeAndDuration,
        bookedData,
        bookActivityForm,
        availableParticipantsData
      );
    }

    selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      bookActivityForm
    );

    changeFormFieldValue('startTime', selectedStartTime);
  };

  const setParticipants = activity => {
    const previousParticipants = bookActivityForm.values.participants || [];
    const participants = getParticipants(
      activity,
      bookActivityForm,
      setIsUpdatedParticipants,
      isUpdatedParticipants,
      bookActivityForm.values.currency,
      previousParticipants
    );

    changeFormFieldValue('participants', participants);
  };

  const setAddOns = activity => {
    const previousAddOns = bookActivityForm.values.addOns || [];

    const addOnsList = activity.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });

    changeFormFieldValue('addOns', addOnsList);
  };

  const setMaxParticipants = seatsLeft => {
    const maxParticipants = getMaxParticipants(
      seatsLeft,
      bookedData,
      bookActivityForm,
      availableParticipantsData
    );

    changeFormFieldValue('maxParticipants', maxParticipants);
  };

  const changeMonthHandler = date => {
    refetchParticipantsData({
      activityId: activityId || parseInt(id),
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const submit = () => {
    setLoading(true);

    if (
      bookActivityForm.values.participants &&
      bookActivityForm.values.participants.length
    ) {
      bookActivityForm.values.participants = [
        {
          ...bookActivityForm.values.participants[0],
          participants: bookActivityForm.values.maxParticipants
        }
      ];
    }

    const data = getPreparedBookActivityData(
      activity,
      bookActivityForm,
      deletedRelatedAccommodations
    );

    if (isUpdate) {
      updateActivityBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(t('Activity booking has been successfully updated'), {
            variant: 'success'
          });
          history.push({
            pathname: '/bookings-list'
          });
        },
        () => {
          setLoading(false);
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    } else {
      createActivityBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(t('Activity has been successfully booked'), {
            variant: 'success'
          });
          history.push({
            pathname: '/activities'
          });
        },
        () => {
          setLoading(false);
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    }
  };

  const handlerSubmitWrapper = props => {
    if (bookActivityForm.syncErrors) {
      const syncErrors = bookActivityForm.syncErrors;
      const errorsLength = Object.keys(syncErrors).length;

      if (syncErrors.isMaxParticipantsErrors && errorsLength === 2) {
        const error = syncErrors.participants['_error'];
        const errorSplit = error.split(' ');
        const errorLength = errorSplit.length;
        enqueueSnackbar(
          t('The number of Participants can’t exceed', {
            number: errorSplit[errorLength - 1]
          }),
          {
            variant: 'warning'
          }
        );
      } else {
        enqueueSnackbar(t('Please fill in all the required fields'), {
          variant: 'warning'
        });
      }
    }

    handleSubmit(submit)(props);
  };

  return (
    <>
      <AppHeader />

      <CircularLoader
        isLoading={loading || isAvailableParticipantsLoading || isLoading}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      {activity && (
        <div className={classes.root}>
          <div className={classes.leftSide}>
            <Typography variant="h3" classes={{ root: classes.productCode }}>
              {t('Product code')}: {activity.generalForm.productCode}
            </Typography>

            <div
              className={classes.photo}
              style={{
                backgroundImage: `url(${activity.generalForm.coverPhoto.image})`
              }}
            >
              {''}
            </div>

            <Typography variant="h2" className={classes.fontWeight}>
              {
                getFormByCurrentLanguage(
                  selectedLanguage,
                  activity.generalForm,
                  activity['generalForm-Norwegian']
                ).title
              }
            </Typography>

            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {activity.generalForm.startingPoint}
                </Typography>
              </div>
            </div>

            <Typography variant="body1" classes={{ root: classes.overview }}>
              {
                getFormByCurrentLanguage(
                  selectedLanguage,
                  activity.generalForm,
                  activity['descriptionForm-Norwegian']
                ).overview
              }
            </Typography>

            <BookInfoList
              activity={activity}
              currency={bookActivityForm.values.currency}
              defaultExpanded={true}
            />
          </div>

          <div className={classes.rightSide}>
            <form
              onSubmit={handlerSubmitWrapper}
              className={classes.bookWrapper}
            >
              <Typography
                variant="h1"
                className={classNames(classes.fontWeight, classes.inputIndent)}
              >
                {isUpdate ? t('Update Activity Booking') : t('Book Activity')}
              </Typography>

              {activity.scheduleType === SCHEDULE_DATE_NO_TIME_NAME && (
                <Field
                  name="multiDates"
                  label="Starting date *"
                  availableRange={getAvailableRangeDates(activity)}
                  disabledDates={getDisabledDates(
                    availableParticipantsData,
                    null,
                    activity
                  )}
                  changeMonthsCallback={changeMonthHandler}
                  numberOfMonths={2}
                  daySize={39}
                  initialFocus="startDate"
                  component={MultiDatePickerCalendar}
                  dateRangePickerClass="dateRangeOpenedPicker"
                  inputClassName={classes.disableInputFocus}
                  cutoffData={activity[activity.scheduleType]}
                />
              )}

              {activity.scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <Field
                  name="startingDate"
                  label="Starting date *"
                  availableRange={getAvailableRangeDates(activity)}
                  disabledDates={getDisabledDates(
                    availableParticipantsData,
                    null,
                    activity
                  )}
                  changeMonthsCallback={changeMonthHandler}
                  numberOfMonths={2}
                  daySize={39}
                  component={SingleDatePicker}
                  cutoffData={activity[activity.scheduleType]}
                />
              )}

              <FormActionButtons
                cancelPath={isUpdate ? '/bookings-list' : '/activities'}
                actionLabel={isUpdate ? 'Update' : 'Book'}
              />
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => {
  return {
    bookActivityForm: state.form.bookActivityForm,
    businessOwner: state.businessOwner
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookActivityForm', field, value));
  }
});

export default reduxForm({
  form: 'bookActivityForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(BookActivity));
